/**
 * The end-of-rest alert, and everything iOS makes awkward about it.
 *
 * Two platform facts shape all of this:
 *
 * 1. Safari on iOS implements no Vibration API, so `navigator.vibrate` is a no-op on an
 *    iPhone however it is called. Sound is the only alert a web page can raise there, and
 *    a notification is the only thing that can make the phone itself buzz.
 * 2. A backgrounded page's timers stop. So the sound is *scheduled on the audio clock* the
 *    moment the rest starts, rather than played when the countdown reaches zero: CoreAudio
 *    keeps rendering an already-scheduled buffer while the page that queued it is frozen,
 *    where a `setTimeout` would simply never fire.
 *
 * Scheduling has to happen inside the tap that starts the rest — a context created outside
 * a gesture stays suspended — and a near-silent keep-alive loop holds the audio session open,
 * because a context with nothing playing is suspended on backgrounding and never reaches the
 * beep. None of this is guaranteed: another app taking the audio session, or a long enough
 * suspension, can still swallow it, which is why the countdown also alerts on its own account
 * when the app comes back to the foreground and finds the rest already over.
 */

type Ctor = typeof AudioContext;

const audioCtor = (): Ctor | undefined =>
  typeof window === "undefined" ? undefined : window.AudioContext || (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext;

let context: AudioContext | null = null;
let keepAlive: AudioBufferSourceNode | null = null;
let scheduled: OscillatorNode[] = [];
/** Wall-clock time the queued sound is due, which is what says whether it can have played. */
let dueAt = 0;

/** Whether a sound can be raised at all. Older WebViews have neither constructor. */
export const alarmSupported = () => Boolean(audioCtor());

/** Must be reached from a user gesture the first time, or the context stays suspended. */
function open(): AudioContext | null {
  const Ctor = audioCtor();
  if (!Ctor) return null;
  if (!context) {
    try { context = new Ctor(); } catch { return null; }
  }
  // Returns a promise that rejects when there is no gesture behind the call; nothing to do
  // about that here, and the next tap will resume it.
  if (context.state !== "running") void context.resume().catch(() => undefined);
  return context;
}

/**
 * Two seconds of silence on a loop. The point is not the sound but the audio session:
 * a context rendering nothing is suspended when the app goes to the background, and a
 * suspended context's clock does not reach the beep that was queued on it.
 */
function holdSession(ctx: AudioContext) {
  if (keepAlive) return;
  const buffer = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * 2)), ctx.sampleRate);
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  source.connect(ctx.destination);
  source.start();
  keepAlive = source;
}

function releaseSession() {
  try { keepAlive?.stop(); } catch { /* already stopped */ }
  keepAlive?.disconnect();
  keepAlive = null;
}

/** Three tones, the last one held, at `offset` seconds on the context's own clock. */
function beep(ctx: AudioContext, at: number, offset: number, length: number, frequency: number) {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = "sine";
  oscillator.frequency.value = frequency;
  // An abrupt start and stop is heard as a click, so the envelope ramps at both ends.
  gain.gain.setValueAtTime(0, at + offset);
  gain.gain.linearRampToValueAtTime(0.5, at + offset + 0.01);
  gain.gain.setValueAtTime(0.5, at + offset + length - 0.04);
  gain.gain.linearRampToValueAtTime(0, at + offset + length);
  oscillator.connect(gain).connect(ctx.destination);
  oscillator.start(at + offset);
  oscillator.stop(at + offset + length + 0.02);
  oscillator.onended = () => { scheduled = scheduled.filter((node) => node !== oscillator); };
  scheduled.push(oscillator);
}

const PATTERN: Array<[offset: number, length: number, frequency: number]> = [
  [0, 0.15, 880],
  [0.25, 0.15, 880],
  [0.5, 0.45, 1175]
];

/**
 * Queue the alert for `seconds` from now. Call this from the tap that starts the rest.
 * Returns false when no audio is available, so the caller can say so rather than
 * leaving someone waiting for a sound that was never possible.
 */
export function scheduleAlarm(seconds: number): boolean {
  const ctx = open();
  if (!ctx) return false;
  cancelAlarm({ release: false });
  holdSession(ctx);
  const wait = Math.max(0, seconds);
  const at = ctx.currentTime + wait;
  for (const [offset, length, frequency] of PATTERN) beep(ctx, at, offset, length, frequency);
  dueAt = Date.now() + wait * 1000;
  return true;
}

/** Sound the alert now — the fallback for a scheduled one the audio session swallowed. */
export function playAlarm(): boolean {
  return scheduleAlarm(0);
}

/**
 * Whether the queued sound can be trusted to have played on time.
 *
 * The deciding evidence is how late we are noticing. A page that stayed awake notices its own
 * countdown reaching zero within a fraction of a second, and the sound queued for that moment
 * is playing as we ask — replaying it there would chop the tone in half. A page that was
 * suspended notices on waking, arbitrarily late, and by then a context whose clock was stopped
 * never reached the sound at all; that is the case worth playing now.
 */
export function alarmSounded(now = Date.now()): boolean {
  if (!dueAt || context?.state !== "running") return false;
  const late = now - dueAt;
  return late >= 0 && late < 2000;
}

/**
 * Drop a queued alert. `release` also gives up the audio session, which is right when the
 * rest is over or abandoned but wrong between two scheduled rests: reacquiring the session
 * needs a gesture, and by then there may not be one.
 */
export function cancelAlarm({ release = true }: { release?: boolean } = {}) {
  for (const node of scheduled) {
    try { node.stop(); } catch { /* already ended */ }
    node.disconnect();
  }
  scheduled = [];
  dueAt = 0;
  if (release) releaseSession();
}

/** Whether asking for notification permission is even possible here. */
export const notificationsSupported = () => typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator;

export const notificationPermission = (): NotificationPermission | "unsupported" =>
  notificationsSupported() ? Notification.permission : "unsupported";

/**
 * Ask for permission to post notifications.
 *
 * On iOS this only succeeds once the app has been added to the Home Screen and opened from
 * there — Safari tabs cannot be granted it — so a refusal here is as likely to mean "not
 * installed" as "declined".
 */
export async function askForNotifications(): Promise<NotificationPermission | "unsupported"> {
  if (!notificationsSupported()) return "unsupported";
  if (Notification.permission !== "default") return Notification.permission;
  try { return await Notification.requestPermission(); } catch { return Notification.permission; }
}

const REST_TAG = "rubymax-rest";

/**
 * Post the rest-is-over notification, which is the one alert that reaches the phone itself
 * — a banner, its sound, and the system's own haptic — rather than only the page.
 *
 * It is posted through the service worker registration because a page-constructed
 * `new Notification()` is not supported where a PWA needs it. It can only be posted while
 * this code is running, so it lands promptly when the page is still alive in the background
 * and on return otherwise. A notification at a deadline the page slept through needs a push
 * from a server, which is a different piece of machinery entirely.
 */
export async function notifyRestOver(body: string) {
  if (notificationPermission() !== "granted") return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    await registration?.showNotification("Rest is over", {
      body,
      tag: REST_TAG, // Replaces the previous one instead of stacking a banner per set.
      icon: "/icon-192.png",
      badge: "/icon-192.png"
    });
  } catch { /* a notification is a courtesy; failing to post one is not worth reporting */ }
}

/**
 * The push half of the rest alert: the only one that arrives at the deadline itself.
 *
 * `notifyRestOver` can only post a banner while this code is running, and switching apps
 * freezes the page, so on a phone it very often posts on your return rather than on time.
 * The server has no such problem. On every rest start the deadline and this device's push
 * subscription go up to a Durable Object, which pushes at the second whatever the phone is
 * doing; the banner it raises carries the same tag, so it replaces the local one instead of
 * stacking with it when the app did happen to be awake.
 *
 * All of it is a courtesy on top of the tone. A deployment with no VAPID key configured,
 * a browser with no push support and a phone with no signal each end up here doing nothing,
 * and none of them is worth interrupting a workout to report.
 */
let subscribing: Promise<PushSubscriptionJSON | null> | null = null;

async function pushTarget(): Promise<PushSubscriptionJSON | null> {
  if (notificationPermission() !== "granted") return null;
  // Held across rests: subscribing re-checks with the push service, and this runs on every tap.
  subscribing ??= (async () => {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.pushManager) return null;
    const existing = await registration.pushManager.getSubscription();
    if (existing) return existing.toJSON();
    const { key } = await fetch("/api/push/key", { credentials: "same-origin" }).then((response) => response.json()) as { key?: string };
    if (!key) return null;
    const bytes = Uint8Array.from(atob(key.replace(/-/g, "+").replace(/_/g, "/")), (character) => character.charCodeAt(0));
    return (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytes })).toJSON();
  })().catch(() => null);
  const target = await subscribing;
  // A failure is not cached: the next rest may be on a connection that can reach the server.
  if (!target) subscribing = null;
  return target;
}

/**
 * Schedule and cancel share one chain so a slow POST cannot land after a later DELETE and
 * re-arm a rest the user already stopped. Each call still resolves on its own; failures stay
 * swallowed because the tone is the alert that matters.
 */
let pushChain: Promise<void> = Promise.resolve();

function enqueuePush(task: () => Promise<void>): Promise<void> {
  const run = pushChain.then(task, task);
  pushChain = run.catch(() => undefined);
  return run;
}

export function schedulePush(seconds: number, body: string): Promise<void> {
  return enqueuePush(async () => {
    try {
      const subscription = await pushTarget();
      if (!subscription) return;
      await fetch("/api/push/rest", {
        method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" },
        body: JSON.stringify({ subscription, at: Date.now() + Math.round(seconds * 1000), body })
      });
    } catch { /* the tone is the alert that matters; this one is a bonus */ }
  });
}

/** Call off a pending push, so a rest you paused or abandoned does not buzz you later. */
export function cancelPush(): Promise<void> {
  return enqueuePush(async () => {
    if (notificationPermission() !== "granted") return;
    try { await fetch("/api/push/rest", { method: "DELETE", credentials: "same-origin" }); }
    catch { /* it will fire and be replaced by the next rest, which is survivable */ }
  });
}

/** Clear the banner once you are looking at the app again. */
export async function clearRestNotification() {
  if (!notificationsSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const open = await registration?.getNotifications({ tag: REST_TAG });
    open?.forEach((notification) => notification.close());
  } catch { /* nothing to clear */ }
}

/** A no-op on iOS, which has no Vibration API. Kept for the platforms that do. */
export const buzz = (pattern: number | number[]) => { navigator.vibrate?.(pattern); };
